import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../../api/auth';
import Logo from '../logo/Logo';
import NotificationBell from '../NotificationBell';
import FeedbackModal from '../feedback/FeedbackModal';
import {
  HamburgerMenuIcon,
  Cross1Icon,
  HomeIcon,
  ClockIcon,
  CalendarIcon,
  PersonIcon,
  ExitIcon,
  ChevronLeftIcon,
  ChatBubbleIcon,
  Share1Icon,
} from '@radix-ui/react-icons';
import { Button } from './Button';
import { cn } from '../../utils/cn';
import { Toaster } from './Toaster';
import { useToast } from '../../hooks/useToast';
import { ThemeSwitcher } from './ThemeSwitcher';

const menuItems = [
  { name: 'Home', icon: HomeIcon, path: '/home' },
  { name: 'Activities', icon: ClockIcon, path: '/activities' },
  { name: 'Calendar', icon: CalendarIcon, path: '/calendar' },
  { name: 'Mind maps', icon: Share1Icon, path: '/mindmaps' },
  { name: 'Profile', icon: PersonIcon, path: '/profile' },
];

const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.message || 'Failed to logout');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-background/80 backdrop-blur-lg border-b border-border z-50">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden"
            >
              <HamburgerMenuIcon className="h-5 w-5" />
            </Button>
            <div className="hidden lg:block">
              <Logo />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <NotificationBell />
            <ThemeSwitcher />
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 z-40">
        {/* Desktop Sidebar */}
        <motion.aside
          initial={false}
          animate={{ 
            width: isSidebarOpen ? 288 : 64,
            transition: { duration: 0.2 }
          }}
          className="hidden lg:flex flex-col h-full bg-background border-r border-border pt-16"
        >
          <div className="flex flex-col h-full relative">
            {/* Collapse Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="absolute -right-3 top-3 h-6 w-6 rounded-full border border-border bg-background shadow-sm"
            >
              <ChevronLeftIcon className={cn(
                "h-4 w-4 transition-transform duration-200",
                !isSidebarOpen && "rotate-180"
              )} />
            </Button>

            <nav className="flex-1 px-3 py-4">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg mb-1 transition-colors relative group overflow-hidden whitespace-nowrap",
                      isActive
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    )}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <motion.span
                      initial={false}
                      animate={{ opacity: isSidebarOpen ? 1 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {item.name}
                    </motion.span>
                    {isActive && (
                      <motion.div
                        layoutId="active-nav"
                        className="absolute left-0 w-1 inset-y-1 bg-primary rounded-full"
                        transition={{ type: "spring", damping: 15 }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="p-3 border-t border-border mt-auto space-y-2">
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start text-muted-foreground hover:text-primary gap-3",
                  !isSidebarOpen && "justify-center"
                )}
                onClick={() => setShowFeedbackModal(true)}
              >
                <ChatBubbleIcon className="h-5 w-5 flex-shrink-0" />
                <motion.span
                  initial={false}
                  animate={{ opacity: isSidebarOpen ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  Send Feedback
                </motion.span>
              </Button>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start text-muted-foreground hover:text-destructive gap-3",
                  !isSidebarOpen && "justify-center"
                )}
                onClick={handleLogout}
              >
                <ExitIcon className="h-5 w-5 flex-shrink-0" />
                <motion.span
                  initial={false}
                  animate={{ opacity: isSidebarOpen ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  Logout
                </motion.span>
              </Button>
            </div>
          </div>
        </motion.aside>

        {/* Mobile Sidebar */}
        <AnimatePresence mode="wait">
          {isSidebarOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
                onClick={() => setIsSidebarOpen(false)}
              />
              <motion.aside
                initial={{ x: -288 }}
                animate={{ x: 0 }}
                exit={{ x: -288 }}
                transition={{ type: "tween", duration: 0.2 }}
                className="absolute left-0 top-0 bottom-0 w-72 bg-background border-r border-border pt-16 z-40 lg:hidden"
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between p-4 lg:hidden">
                    <Logo />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsSidebarOpen(false)}
                    >
                      <Cross1Icon className="h-5 w-5" />
                    </Button>
                  </div>

                  <nav className="flex-1 px-3 py-4">
                    {menuItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={cn(
                            "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mb-1 transition-colors relative group",
                            isActive
                              ? "text-primary bg-primary/10"
                              : "text-muted-foreground hover:text-foreground hover:bg-accent"
                          )}
                          onClick={() => setIsSidebarOpen(false)}
                        >
                          <item.icon className="h-5 w-5" />
                          <span>{item.name}</span>
                          {isActive && (
                            <motion.div
                              layoutId="active-nav-mobile"
                              className="absolute left-0 w-1 inset-y-1 bg-primary rounded-full"
                              transition={{ type: "spring", damping: 15 }}
                            />
                          )}
                        </Link>
                      );
                    })}
                  </nav>

                  <div className="p-4 border-t border-border mt-auto space-y-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-muted-foreground hover:text-primary"
                      onClick={() => {
                        setShowFeedbackModal(true);
                        setIsSidebarOpen(false);
                      }}
                    >
                      <ChatBubbleIcon className="mr-2 h-5 w-5" />
                      Send Feedback
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-muted-foreground hover:text-destructive"
                      onClick={handleLogout}
                    >
                      <ExitIcon className="mr-2 h-5 w-5" />
                      Logout
                    </Button>
                  </div>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Main Content */}
      <main className={cn(
        "pt-16 min-h-screen transition-all duration-200",
        isSidebarOpen ? "lg:pl-72" : "lg:pl-16"
      )}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="container mx-auto p-6"
        >
          {children}
        </motion.div>
      </main>
      <Toaster />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </div>
  );
};

export default Layout; 